import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { SafeStorage } from '../utils/safeStorage';

/**
 * Puts a scroll container back where the user left it.
 *
 * Tapping a group or an account is a round trip: the screen unmounts on the way
 * out and remounts on the way back, so without this the reader is returned to
 * the top of a list they may have scrolled a long way down. Discover had a
 * `scrollContainerRef` wired to its container and never read — the container was
 * ready, the restoring was never built.
 *
 * Restoring is not one assignment, because the position being restored to often
 * does not exist yet. Discover's sections stream in after mount, so at first
 * paint the list can be shorter than the saved offset and the browser silently
 * clamps to whatever fits. The retry below re-applies while the content is
 * still growing and stops as soon as it lands.
 *
 * Saving is throttled through a ref rather than state: this fires on every
 * scroll frame, and re-rendering the screen on each one would cost far more
 * than the feature is worth.
 */

/** How long to keep trying before accepting the list will not get any taller. */
const RESTORE_WINDOW_MS = 1200;
/** Gap between restore attempts while content is still arriving. */
const RESTORE_RETRY_MS = 60;
/** Positions older than this are stale; the feed has moved on. */
const MAX_AGE_MS = 30 * 60 * 1000;

interface StoredPosition {
  top: number;
  at: number;
}

export function useScrollRestoration(
  /** Distinct per list. Two lists sharing a key would restore each other's offset. */
  key: string,
  ref: React.RefObject<HTMLElement | null>,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const storageKey = `scroll_pos_${key}`;
  const latestTopRef = useRef(0);
  const restoredRef = useRef(false);

  const save = useCallback((top: number) => {
    try {
      if (top <= 0) {
        SafeStorage.removeItem(storageKey);
        return;
      }
      SafeStorage.setItem(storageKey, JSON.stringify({ top, at: Date.now() } as StoredPosition));
    } catch {
      /* storage unavailable: the list simply opens at the top */
    }
  }, [storageKey]);

  /** Attach to the container's onScroll. */
  const onScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    latestTopRef.current = (event.target as HTMLElement).scrollTop;
  }, []);

  // Restore before paint, so the list never shows the top first and jumps.
  useLayoutEffect(() => {
    if (!enabled || restoredRef.current) return;

    let raw: string | null = null;
    try {
      raw = SafeStorage.getItem(storageKey);
    } catch {
      return;
    }
    if (!raw) return;

    let target = 0;
    try {
      const parsed = JSON.parse(raw) as StoredPosition;
      if (!parsed || typeof parsed.top !== 'number') return;
      if (Date.now() - (parsed.at || 0) > MAX_AGE_MS) {
        SafeStorage.removeItem(storageKey);
        return;
      }
      target = parsed.top;
    } catch {
      // A bare number from an older format, or corrupt. Either way, drop it.
      SafeStorage.removeItem(storageKey);
      return;
    }
    if (target <= 0) return;

    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const attempt = () => {
      const el = ref.current;
      if (!el) return;

      el.scrollTop = target;
      latestTopRef.current = el.scrollTop;

      // Landed, or the list is as tall as it is ever going to get.
      const reached = Math.abs(el.scrollTop - target) < 2;
      if (reached || Date.now() - startedAt > RESTORE_WINDOW_MS) {
        restoredRef.current = true;
        return;
      }
      timer = setTimeout(attempt, RESTORE_RETRY_MS);
    };

    attempt();
    return () => { if (timer) clearTimeout(timer); };
  }, [enabled, storageKey, ref]);

  // Save on the way out. Reading the live container first and falling back to
  // the last onScroll value covers the case where the element is already
  // detached, or reads 0, by the time the cleanup runs.
  useEffect(() => {
    if (!enabled) return;
    return () => {
      const live = ref.current?.scrollTop ?? 0;
      save(live > 0 ? live : latestTopRef.current);
    };
  }, [enabled, ref, save]);

  return { onScroll };
}
